import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import c from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import Slider from '@wix/da-ds/pkg/Slider';
import ProfileFolderCard from '../ProfileFolderCard';
import AddNewFolderCard from '../AddNewFolderCard';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { filterOutEmpty } from '../../../../utils/gallection';
import { useGallectionFolders } from '../hooks/useGallectionFolders';
import { getCanCreateGalleryFolder } from '../../../../selectors/privs';
import { PapiGallection } from '@wix/da-papi-types';
import {
  getProfileOwnerUser,
  isCurrentUserProfileOwner,
} from '../../../../selectors/users';
import { setSelectedSubfolderId } from '../../../../actions/gallectionSection';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './FolderSlider.scss';

export interface Props {
  className?: string;
  cardWidth?: number;
  folder?: PapiGallection | null;
  subfolder?: PapiGallection | null;
  unsetSelectedSubfolderId: () => void;
}

const FolderSlider: FC<Props> = ({
  className,
  cardWidth: cardWidthFromProps,
  folder,
  subfolder,
  unsetSelectedSubfolderId,
}) => {
  const dispatch = useDispatch();
  const hasNewFolderFeature = useFlag('folders_layout');
  const canCreateFolder = useSelector(getCanCreateGalleryFolder);
  const isProfileOwner = useSelector(isCurrentUserProfileOwner);
  const profileOwnerUser = useSelector(getProfileOwnerUser);

  const {
    hasMore,
    hasLess,
    fetchNext,
    fetchPrevious,
    isFetching,
    folders,
    selectedFolderId,
  } = useGallectionFolders();

  const selectedFolderIndex = useMemo(() => {
    return folders.findIndex(({ folderId }) => folderId === selectedFolderId);
  }, [folders, selectedFolderId]);

  const selectedFolder = useMemo(
    () => folders[selectedFolderIndex],
    [folders, selectedFolderIndex]
  );

  const isShowingSubfolders = selectedFolder?.hasSubfolders;

  const foldersToRender = useMemo(() => {
    if (hasNewFolderFeature) {
      const subfolders = isShowingSubfolders ? selectedFolder.subfolders : [];
      if (isShowingSubfolders && subfolders === undefined) {
        window.location.reload();
        return [];
      }
      const shownSubfolders = isProfileOwner
        ? subfolders
        : filterOutEmpty(subfolders);
      if (shownSubfolders && shownSubfolders.length > 0) {
        return [selectedFolder].concat(shownSubfolders);
      } else {
        return isProfileOwner ? folders : filterOutEmpty(folders);
      }
      // if (hasNewFolderFeature && selectedFolder?.hasSubfolders) {
    } else {
      return isProfileOwner ? folders : filterOutEmpty(folders);
    }
  }, [
    hasNewFolderFeature,
    isShowingSubfolders,
    selectedFolder,
    isProfileOwner,
    folders,
  ]);

  const isMobile = useContext(MobileContext);
  const cardWidth = useMemo(() => {
    return cardWidthFromProps
      ? cardWidthFromProps
      : !hasNewFolderFeature
        ? parseInt(s['folder-card-width'])
        : isMobile
          ? parseInt(s['folders_layout_folder-card-width-mobile'])
          : parseInt(s['folders_layout_folder-card-width']);
  }, [cardWidthFromProps, hasNewFolderFeature, isMobile]);
  const scrollToIndex = foldersToRender.findIndex(({ folderId }) => {
    if (hasNewFolderFeature) {
      if (!subfolder) {
        return folder && folderId === folder.folderId;
      } else {
        return folderId === subfolder.folderId;
      }
    } else {
      return folderId === selectedFolderId;
    }
  });

  return (
    <Slider
      className={c(
        s['folders-slider'],
        isMobile && s['mobile'],
        className,
        hasNewFolderFeature && s['folders_layout']
      )}
      scrollToIndex={scrollToIndex}
      smoothInitialScroll={false}
    >
      {hasLess && (
        <Waypoint onEnter={fetchPrevious} horizontal topOffset="-20%" />
      )}
      {foldersToRender.map((f, index) => {
        let onClick: (() => void) | null = null;
        let isSelected = false;
        const isParent: boolean =
          f.folderId === folder?.folderId && folder?.hasSubfolders;

        if (hasNewFolderFeature) {
          if (f.parentId === null) {
            // This is the parent folder, clicking on it should take us back to not having
            // any subfolders selected
            isSelected = subfolder === null;
            onClick = () => {
              unsetSelectedSubfolderId();
            };
          } else {
            if (subfolder && f.folderId === subfolder.folderId) {
              isSelected = true;
            }
            onClick = () => {
              dispatch(setSelectedSubfolderId(f.folderId));
            };
          }

          if (!subfolder && folder && f.folderId === folder.folderId) {
            isSelected = true;
          }
        } else {
          if (folder && f.folderId === folder.folderId) {
            isSelected = true;
          }
        }
        return (
          <ProfileFolderCard
            isSelected={isSelected}
            withPrice
            width={cardWidth}
            className={s['folder-card']}
            key={f.folderId}
            folder={f}
            indexInsideParent={index}
            withLink
            isProfileOwner={isProfileOwner}
            profileOwnerUser={profileOwnerUser}
            onClick={onClick}
            isParent={isParent}
          />
        );
      })}
      {isFetching && <LoadingIndicator className={s['folder-card']} />}
      {hasMore && (
        <Waypoint onEnter={fetchNext} horizontal bottomOffset="-20%" />
      )}
      {canCreateFolder && (!hasNewFolderFeature || !isShowingSubfolders) && (
        <AddNewFolderCard className={s['folder-card']} width={cardWidth} />
      )}
    </Slider>
  );
};
FolderSlider.displayName = 'FolderSlider';

export default FolderSlider;
